import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, ShieldCheck, TrendingUp } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { IMAGES } from "../../data/mock";

const Hero = () => {
  const { lang } = useLanguage();
  const t = translations[lang].hero;

  const stats = [
    { value: "7/24", label: t.stat1 },
    { value: "0\u20ba", label: t.stat2 },
    { value: "%40", label: t.stat3 },
  ];

  return (
    <section className="relative bg-navy-950 overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      {/* background layers */}
      <div className="absolute inset-0 grid-pattern opacity-60" />
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="absolute top-1/3 -left-40 w-[30rem] h-[30rem] rounded-full bg-navy-600/30 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* left */}
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-semibold text-amber-400">
              <span className="w-2 h-2 rounded-full bg-amber-400 pulse-dot" />
              {t.badge}
            </span>
            <h1 className="mt-6 font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {t.title1}{" "}
              <span className="text-amber-400">{t.title2}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
              {t.desc}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/iletisim"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-7 py-3.5 rounded-lg transition-colors shadow-xl shadow-amber-500/25"
              >
                {t.ctaPrimary}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/hizmetlerimiz"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-amber-500/50 hover:bg-white/5 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors"
              >
                {t.ctaSecondary}
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display font-bold text-2xl sm:text-3xl text-white">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs sm:text-sm text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* right visual */}
          <div className="relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src={IMAGES.heroDashboard}
                alt="Canlı altyapı izleme paneli"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl bg-navy-900/80 backdrop-blur-md border border-white/10 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 pulse-dot" />
                  <div>
                    <div className="text-sm font-semibold text-white">{t.liveTitle}</div>
                    <div className="text-xs text-slate-400">{t.liveSub}</div>
                  </div>
                </div>
                <Activity className="w-5 h-5 text-amber-400" />
              </div>
            </div>

            {/* floating badges */}
            <div className="absolute -top-5 -left-5 hidden sm:flex items-center gap-2 rounded-xl bg-white shadow-xl px-4 py-3 animate-float">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-semibold text-navy-900">Zero-Trust</span>
            </div>
            <div
              className="absolute -bottom-5 -right-4 hidden sm:flex items-center gap-2 rounded-xl bg-white shadow-xl px-4 py-3 animate-float"
              style={{ animationDelay: "1.2s" }}
            >
              <TrendingUp className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-semibold text-navy-900">%40 {lang === "tr" ? "Tasarruf" : "Savings"}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
